// CoachMarkdown — a small, dependency-free markdown → React renderer for the
// AI Coach's replies. The model emits GitHub-flavoured markdown; this turns it
// into real formatting (paragraphs, bold/italic, lists, headings, tables, code,
// blockquotes, links, rules) styled with the coach's design tokens — so replies
// read like Claude/ChatGPT instead of showing literal `*` and `**` markers.
//
// Two passes: a block pass (split lines into typed blocks) and an inline pass
// (bold/italic/code/links within a line). Kept deliberately simple — no nesting
// of lists, which the coach never produces.

import React from "react";

const isTableLine = (l) => /^\s*\|.*\|\s*$/.test(l);
const isSeparator = (l) => /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)+\|?\s*$/.test(l);
const splitCells = (row) => row.replace(/^\s*\|/, "").replace(/\|\s*$/, "").split("|").map((c) => c.trim());

// ── Inline pass ────────────────────────────────────────────────────────────
// Tokenise a single string into bold / italic / inline-code / link / text.
function renderInline(str, ctx, keyPrefix = "i") {
  const { boldColor, fontMono, C } = ctx;
  const out = [];
  // One alternation: `code` | **bold** | *italic* | _italic_ | [text](url)
  const re = /(`[^`]+`|\*\*[^*]+\*\*|\*[^*\s][^*]*\*|_[^_\s][^_]*_|\[[^\]]+\]\([^)]+\))/g;
  let last = 0, m, n = 0;
  const pushText = (t) => { if (t) out.push(<React.Fragment key={`${keyPrefix}t${n++}`}>{t}</React.Fragment>); };
  while ((m = re.exec(str)) !== null) {
    if (m.index > last) pushText(str.slice(last, m.index));
    const tok = m[0];
    const key = `${keyPrefix}m${n++}`;
    if (tok[0] === "`") {
      out.push(<code key={key} style={{ fontFamily: fontMono, fontSize: 12.5, background: C.bg3, padding: "1px 5px", borderRadius: 5, color: C.ink1 }}>{tok.slice(1, -1)}</code>);
    } else if (tok.startsWith("**")) {
      out.push(<strong key={key} style={{ color: boldColor, fontWeight: 600 }}>{tok.slice(2, -2)}</strong>);
    } else if (tok[0] === "*" || tok[0] === "_") {
      out.push(<em key={key} style={{ fontStyle: "italic" }}>{tok.slice(1, -1)}</em>);
    } else {
      // [text](url)
      const lm = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(tok);
      if (lm) {
        const url = lm[2];
        out.push(
          <a key={key} style={{ color: C.amber, textDecoration: "underline", cursor: "pointer" }}
            onClick={(e) => { e.preventDefault(); try { window.open(url, "_blank"); } catch {} }}>{lm[1]}</a>
        );
      } else pushText(tok);
    }
    last = m.index + tok.length;
  }
  if (last < str.length) pushText(str.slice(last));
  return out;
}

// ── Block pass ───────────────────────────────────────────────────────────────
export default function CoachMarkdown({ text, C, fontSans, fontSerif, fontMono, boldColor }) {
  const ctx = { C, fontMono, boldColor };
  const lines = String(text || "").split("\n");
  const blocks = [];
  let i = 0;

  const headingSize = { 1: 19, 2: 16.5, 3: 14.5 };

  while (i < lines.length) {
    const l = lines[i];
    const next = lines[i + 1] || "";

    // Blank line → spacer (skip; paragraphs already separate)
    if (/^\s*$/.test(l)) { i++; continue; }

    // Fenced code block ```lang ... ```
    const fence = /^\s*```/.test(l);
    if (fence) {
      const body = [];
      i++;
      while (i < lines.length && !/^\s*```/.test(lines[i])) { body.push(lines[i]); i++; }
      i++; // consume closing fence (or EOF)
      blocks.push({ t: "code", text: body.join("\n") });
      continue;
    }

    // GFM table (header + separator)
    if (isTableLine(l) && isSeparator(next)) {
      const tl = [l, next]; i += 2;
      while (i < lines.length && isTableLine(lines[i])) { tl.push(lines[i]); i++; }
      blocks.push({ t: "table", header: splitCells(tl[0]), body: tl.slice(2).map(splitCells) });
      continue;
    }

    // Heading
    const hm = /^(#{1,3})\s+(.*)$/.exec(l);
    if (hm) { blocks.push({ t: "h", level: hm[1].length, text: hm[2] }); i++; continue; }

    // Horizontal rule (standalone, not a table separator)
    if (/^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(l)) { blocks.push({ t: "hr" }); i++; continue; }

    // Blockquote (consecutive > lines)
    if (/^\s*>\s?/.test(l)) {
      const q = [];
      while (i < lines.length && /^\s*>\s?/.test(lines[i])) { q.push(lines[i].replace(/^\s*>\s?/, "")); i++; }
      blocks.push({ t: "quote", text: q.join("\n") });
      continue;
    }

    // Unordered list (-, *, •)
    if (/^\s*[-*•]\s+/.test(l)) {
      const items = [];
      while (i < lines.length && /^\s*[-*•]\s+/.test(lines[i])) { items.push(lines[i].replace(/^\s*[-*•]\s+/, "")); i++; }
      blocks.push({ t: "ul", items });
      continue;
    }

    // Ordered list (1. 2. …)
    if (/^\s*\d+\.\s+/.test(l)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) { items.push(lines[i].replace(/^\s*\d+\.\s+/, "")); i++; }
      blocks.push({ t: "ol", items });
      continue;
    }

    // Paragraph — gather consecutive non-blank, non-special lines
    const para = [l]; i++;
    while (i < lines.length && !/^\s*$/.test(lines[i])
      && !/^\s*```/.test(lines[i])
      && !/^(#{1,3})\s+/.test(lines[i])
      && !/^\s*[-*•]\s+/.test(lines[i])
      && !/^\s*\d+\.\s+/.test(lines[i])
      && !/^\s*>\s?/.test(lines[i])
      && !(isTableLine(lines[i]) && isSeparator(lines[i + 1] || ""))) {
      para.push(lines[i]); i++;
    }
    blocks.push({ t: "p", text: para.join("\n") });
  }

  const lastIdx = blocks.length - 1;
  return blocks.map((b, bi) => {
    const isFirst = bi === 0, isLast = bi === lastIdx;
    if (b.t === "p") {
      return <p key={bi} style={{ margin: `0 0 ${isLast ? 0 : 10}px`, whiteSpace: "pre-wrap" }}>{renderInline(b.text, ctx, `p${bi}`)}</p>;
    }
    if (b.t === "h") {
      return <div key={bi} style={{ fontFamily: fontSerif, fontStyle: "italic", color: C.ink1, fontSize: headingSize[b.level] || 14.5, lineHeight: 1.25, margin: `${isFirst ? 0 : 14}px 0 6px` }}>{renderInline(b.text, ctx, `h${bi}`)}</div>;
    }
    if (b.t === "ul" || b.t === "ol") {
      return (
        <div key={bi} style={{ margin: `0 0 ${isLast ? 0 : 10}px`, display: "flex", flexDirection: "column", gap: 6 }}>
          {b.items.map((it, k) => (
            <div key={k} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <span style={{ color: C.amber, flexShrink: 0, fontFamily: b.t === "ol" ? fontMono : "inherit", fontSize: b.t === "ol" ? 12 : 14, lineHeight: 1.6, minWidth: b.t === "ol" ? 16 : "auto" }}>
                {b.t === "ol" ? `${k + 1}.` : "•"}
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>{renderInline(it, ctx, `li${bi}_${k}`)}</span>
            </div>
          ))}
        </div>
      );
    }
    if (b.t === "code") {
      return (
        <div key={bi} style={{ background: C.bg3, border: `0.5px solid ${C.line}`, borderRadius: 10, padding: "10px 12px", overflowX: "auto", margin: `8px 0 ${isLast ? 0 : 10}px`, WebkitOverflowScrolling: "touch" }}>
          <pre style={{ margin: 0 }}><code style={{ fontFamily: fontMono, fontSize: 12, lineHeight: 1.5, color: C.ink2, whiteSpace: "pre" }}>{b.text}</code></pre>
        </div>
      );
    }
    if (b.t === "quote") {
      return <div key={bi} style={{ borderLeft: `2px solid ${C.amber}55`, padding: "2px 0 2px 12px", margin: `8px 0 ${isLast ? 0 : 10}px`, color: C.ink3, fontStyle: "italic", whiteSpace: "pre-wrap" }}>{renderInline(b.text, ctx, `q${bi}`)}</div>;
    }
    if (b.t === "hr") {
      return <div key={bi} style={{ borderTop: `0.5px solid ${C.line}`, margin: "14px 0" }} />;
    }
    if (b.t === "table") {
      return (
        <div key={bi} style={{ overflowX: "auto", margin: `8px 0 ${isLast ? 0 : 10}px`, WebkitOverflowScrolling: "touch" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12.5, fontFamily: "inherit", lineHeight: 1.45 }}>
            <thead>
              <tr>
                {b.header.map((h, hi) => (
                  <th key={hi} style={{ textAlign: "left", padding: "6px 10px", fontFamily: fontMono, fontSize: 9.5, letterSpacing: "0.14em", textTransform: "uppercase", color: C.amber, fontWeight: 700, borderBottom: `0.5px solid ${C.amber}33`, whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {b.body.map((row, ri) => (
                <tr key={ri}>
                  {row.map((cell, ci) => (
                    <td key={ci} style={{ padding: "7px 10px", borderBottom: ri === b.body.length - 1 ? "none" : `0.5px solid ${C.line}`, verticalAlign: "top", whiteSpace: "nowrap" }}>{renderInline(cell, ctx, `c${bi}_${ri}_${ci}`)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    return null;
  });
}
